/**
 * 계정 관련 전역 상태를 관리하는 Store
 * - 이용자 정보 관리
 * - 회사 정보 관리
 * - 직원 목록 관리
 * - 계정 관련 API 요청 상태 관리
 */
import { useCartStore } from '@/features/cart/stores/cartStore';
import { AxiosError } from 'axios';
import { create } from 'zustand';
import { accountService } from '../services/accountService';
import { Company, Employee, User } from '../types/account';

/**
 * 계정 Store의 상태 및 액션 타입 정의
 */
interface AccountStore {
  // 상태
  user: User | null; // 현재 로그인한 이용자 정보
  company: Company | null; // 이용자의 회사 정보
  employees: Employee[]; // 회사 직원 목록
  isLoading: boolean; // API 요청 로딩 상태
  error: string | null; // 에러 메시지

  // 액션
  fetchUserInfo: () => Promise<void>; // 이용자 정보 조회
  fetchCompanyInfo: () => Promise<void>; // 회사 정보 조회
  fetchCompanyEmployees: () => Promise<void>; // 직원 목록 조회
  approveEmployee: (employeeId: number) => Promise<void>; // 직원 승인
  deactivateEmployee: (employeeId: number) => Promise<void>; // 직원 비활성화
  onChangeUser: (hasUser: boolean) => Promise<void>;
  setOwner: (employeeId: number) => Promise<void>; // 대표 계정 설정
}

export const useAccountStore = create<AccountStore>()((set) => {
  const { fetchCartItems } = useCartStore.getState();

  return {
    user: null,
    company: null,
    employees: [] as Employee[],
    isLoading: false,
    error: null,

    onChangeUser: async (hasUser: boolean) => {
      if (hasUser) {
        await fetchCartItems();
      }
    },

    /**
     * 이용자 정보 조회
     * - API를 통해 현재 로그인한 이용자의 정보를 조회
     * - 조회된 정보로 상태 업데이트
     */
    fetchUserInfo: async () => {
      try {
        set({ isLoading: true, error: null });
        const user = await accountService.getUserInfo();
        set({ user, isLoading: false });
      } catch (error) {
        if (error instanceof AxiosError) {
          set({ error: error.response?.data.message, isLoading: false });
        } else {
          set({
            error: '이용자 정보를 불러오는데 실패했습니다.',
            isLoading: false,
          });
        }
      }
    },

    /**
     * 회사 정보 조회
     * - API를 통해 이용자가 속한 회사 정보를 조회
     * - 조회된 정보로 상태 업데이트
     */
    fetchCompanyInfo: async () => {
      try {
        set({ isLoading: true, error: null });
        const company = await accountService.getCompanyInfo();
        set({ company, isLoading: false });
      } catch (error) {
        if (error instanceof AxiosError) {
          set({ error: error.response?.data.message, isLoading: false });
        } else {
          set({
            error: '회사 정보를 불러오는데 실패했습니다.',
            isLoading: false,
          });
        }
      }
    },

    /**
     * 직원 목록 조회
     * - API를 통해 회사의 전체 직원 목록을 조회
     * - 조회된 정보를 Employee 타입으로 변환하여 상태 업데이트
     */
    fetchCompanyEmployees: async () => {
      try {
        set({ isLoading: true, error: null });
        const employees = await accountService.getCompanyMembers();
        // API 응답을 Employee 타입에 맞게 변환
        set({ employees: employees as Employee[], isLoading: false });
      } catch (error) {
        if (error instanceof AxiosError) {
          set({ error: error.response?.data.message, isLoading: false });
        } else {
          set({
            error: '직원 목록을 불러오는데 실패했습니다.',
            isLoading: false,
          });
        }
      }
    },

    /**
     * 직원 승인
     * @param employeeId - 승인할 직원의 ID
     */
    approveEmployee: async (employeeId: number) => {
      try {
        set({ isLoading: true, error: null });
        await accountService.approveMember(employeeId);
      } catch (error) {
        if (error instanceof AxiosError) {
          set({ error: error.response?.data.message, isLoading: false });
        } else {
          set({ error: '직원 승인에 실패했습니다.', isLoading: false });
        }
      }
    },

    /**
     * 직원 비활성화
     * @param employeeId - 비활성화할 직원의 ID
     */
    deactivateEmployee: async (employeeId: number) => {
      try {
        set({ isLoading: true, error: null });
        await accountService.deactivateMember(employeeId);
      } catch (error) {
        if (error instanceof AxiosError) {
          set({ error: error.response?.data.message, isLoading: false });
        } else {
          set({ error: '직원 비활성화에 실패했습니다.', isLoading: false });
        }
      }
    },

    /**
     * 대표 계정 설정
     * @param employeeId - 대표 계정으로 설정할 직원의 ID
     */
    setOwner: async (employeeId: number) => {
      try {
        set({ isLoading: true, error: null });
        await accountService.setOwner(employeeId);
        set({ isLoading: false, error: null });
      } catch (error) {
        if (error instanceof AxiosError) {
          throw error;
        } else {
          throw '대표 계정 설정에 실패했습니다.';
        }
      }
    },
  };
});
